import type { ClientMessage, ServerMessage } from '@protocol';

export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface RoomClientOptions {
  code: string;
  clientId: string;
  onMessage(msg: ServerMessage): void;
  onStatus(status: SocketStatus): void;
}

const PING_INTERVAL_MS = 20_000;

const BACKOFF_MS = [250, 500, 1000, 2000, 4000, 8000] as const;

export class RoomClient {
  #ws: WebSocket | null = null;
  #opts: RoomClientOptions;
  #attempt = 0;
  #closedByUs = false;
  #pingTimer: ReturnType<typeof setInterval> | null = null;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #offsetMs = 0;
  #haveOffset = false;
  #seq = 0;

  constructor(opts: RoomClientOptions) {
    this.#opts = opts;
  }

  now(): number {
    return Date.now() + this.#offsetMs;
  }

  get hasClockOffset(): boolean {
    return this.#haveOffset;
  }

  delayUntil(serverTimestamp: number): number {
    return Math.max(0, serverTimestamp - this.now());
  }

  connect(): void {
    this.#closedByUs = false;
    this.#opts.onStatus(this.#attempt === 0 ? 'connecting' : 'reconnecting');

    const url = new URL(`${location.origin.replace(/^http/, 'ws')}/ws`);
    url.searchParams.set('code', this.#opts.code);

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.#scheduleReconnect();
      return;
    }
    this.#ws = ws;

    ws.onopen = () => {
      this.#attempt = 0;
      this.#opts.onStatus('open');
      this.send({ type: 'join', clientId: this.#opts.clientId });
      this.#startPings();
    };

    ws.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }
      const msg = parsed as ServerMessage | { type: 'pong' };
      if (msg.type === 'pong') return;

      if (msg.type === 'state') {
        this.#offsetMs = msg.state.serverNow - Date.now();
        this.#haveOffset = true;
      } else if (msg.type === 'life-lost') {
        this.#offsetMs = msg.serverNow - Date.now();
        this.#haveOffset = true;
      }
      this.#opts.onMessage(msg as ServerMessage);
    };

    ws.onclose = () => {
      this.#stopPings();
      this.#ws = null;
      if (this.#closedByUs) {
        this.#opts.onStatus('closed');
        return;
      }
      this.#scheduleReconnect();
    };

    ws.onerror = () => {
    };
  }

  nextSeq(): number {
    return ++this.#seq;
  }

  send(msg: ClientMessage): boolean {
    if (this.#ws?.readyState !== WebSocket.OPEN) return false;
    this.#ws.send(JSON.stringify(msg));
    return true;
  }

  close(): void {
    this.#closedByUs = true;
    this.#stopPings();
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    this.#ws?.close();
    this.#ws = null;
    this.#opts.onStatus('closed');
  }

  #scheduleReconnect(): void {
    this.#opts.onStatus('reconnecting');
    const delay = BACKOFF_MS[Math.min(this.#attempt, BACKOFF_MS.length - 1)]!;
    this.#attempt += 1;
    const jittered = delay * (0.75 + Math.random() * 0.5);
    this.#reconnectTimer = setTimeout(() => this.connect(), jittered);
  }

  #startPings(): void {
    this.#stopPings();
    this.#pingTimer = setInterval(() => {
      if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send('{"type":"ping"}');
    }, PING_INTERVAL_MS);
  }

  #stopPings(): void {
    if (this.#pingTimer) clearInterval(this.#pingTimer);
    this.#pingTimer = null;
  }
}
